import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, X } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { LetterheadPanel } from './LetterheadPanel';

const STEPS = {
  practice_details: ['Practice details', 'Legal & trading name, ABN, address, phone, email', '/settings?tab=organization'],
  professional_numbers: ['Professional numbers', 'Tax agent, BAS agent, IPA/CPA/CA membership (all optional)', '/settings?tab=organization'],
  logo: ['Logo & letterhead', 'Upload your logo for letters and emails', '/letters/settings'],
  signatory: ['Signatory', 'Who signs your letters', '/letters/settings'],
  sending_domain: ['Email sending domain', 'Send from your own domain (Settings → Email)', '/settings?tab=email'],
  invite_staff: ['Invite your team', 'Each person sets their own password from an emailed link', '/settings?tab=users'],
  portal_basics: ['Client portal basics', 'Choose which modules your clients see', '/settings?tab=client-portal'],
  compliance_defaults: ['Review compliance defaults', 'Check lodgement and reminder defaults', '/compliance'],
  mfa: ['Two-factor sign-in', 'Protect your account with an authenticator app', '/settings?tab=security'],
};

export const SetupChecklist = ({ force = false }) => {
  const [s, setS] = useState(null);
  const [lhOpen, setLhOpen] = useState(false);
  const load = useCallback(() => api.get('/api/practice/setup').then((r) => setS(r.data)).catch(() => setS(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!s || (s.dismissed && !force) || s.done === s.total) return null;
  const put = (b) => api.put('/api/practice/setup', b).then((r) => setS(r.data));
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="setup-checklist">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold">Set up your practice</h2><span className="text-xs text-muted-foreground" data-testid="setup-progress">{s.done} of {s.total} done</span>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => put({ dismissed: true })} aria-label="Hide setup checklist" data-testid="setup-dismiss"><X className="w-4 h-4" /></Button>
      </div>
      <div className="h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width]" style={{ width: `${(100 * s.done) / s.total}%` }} /></div>
      <ul className="grid sm:grid-cols-2 gap-2">
        {s.steps.map((st) => {
          const [title, hint, to] = STEPS[st.key];
          return (
            <li key={st.key} className="flex items-start gap-2 text-sm" data-testid={`setup-step-${st.key}`}>
              {st.done ? <CheckCircle2 className="w-4 h-4 text-primary mt-0.5" /> : <Circle className="w-4 h-4 text-muted-foreground mt-0.5" />}
              <span className="flex-1">{st.key === 'logo'
                ? <button type="button" className="font-medium hover:underline" onClick={() => setLhOpen(true)} data-testid="setup-open-letterhead">{title}</button>
                : <Link to={to} className="font-medium hover:underline">{title}</Link>}{st.key === 'mfa' && s.mfa_required ? ' (required)' : ''}
                <span className="block text-xs text-muted-foreground">{hint}</span></span>
              {!st.auto && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => put({ step: st.key, done: !st.done })} data-testid={`setup-mark-${st.key}`}>{st.done ? 'Undo' : 'Mark done'}</Button>}
            </li>
          );
        })}
      </ul>
      <LetterheadPanel open={lhOpen} onOpenChange={setLhOpen} canEdit={['owner', 'admin'].includes(s.role)} onSaved={load} />
    </div>
  );
};

export const ReopenSetup = () => (
  <Button variant="outline" size="sm" onClick={() => api.put('/api/practice/setup', { dismissed: false }).then(() => { window.location.href = '/dashboard'; })} data-testid="setup-reopen">
    Open setup checklist
  </Button>
);
